import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * When true, anonymous (logged-out) users can access the page.
   * Logged-in users without an active subscription are still redirected to pricing.
   */
  allowAnonymous?: boolean;
}

/**
 * Route wrapper that requires authentication and active subscription.
 * Redirects to /login if not authenticated (unless allowAnonymous is set).
 * Redirects to /pricing if authenticated but subscription requires payment.
 * Shows loading spinner while checking auth state to prevent redirect flicker.
 */
export function ProtectedRoute({
  children,
  allowAnonymous = false,
}: ProtectedRouteProps): React.JSX.Element {
  const { user, subscription, requiresPayment, loading } = useAuth();

  // Show loading spinner while checking auth - prevents redirect flicker
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!user) {
    if (allowAnonymous) {
      return <>{children}</>;
    }
    return <Navigate to="/login" replace />;
  }

  if (requiresPayment) {
    // Distinguish lapsed subscribers (canceled/past_due) from new users (none/incomplete)
    const isResubscribe = ['canceled', 'past_due'].includes(
      subscription?.status ?? ''
    );
    return (
      <Navigate to={isResubscribe ? '/pricing?resubscribe=true' : '/pricing'} replace />
    );
  }

  return <>{children}</>;
}
